import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "HeavyDutyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Specialty Woven Mesh",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Heavy-Duty Woven Wire Mesh",
      lead: "Robust, heavy-gauge stainless steel screening cloth fabricated with precision pre-crimped crimping styles for rigorous aggregate classification, mineral grading, and abrasive solids separation.",
      secondaryCta: {
        label: "Specialty Woven Mesh",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "Industrial heavy-duty stainless steel pre-crimped woven wire mesh",
      },
      cardNote: "Pre-Crimped Rigid Construction",
      cardLink: {
        label: "Screen Sizing Assistance",
        to: "/ContactPage",
      },
      specs: [
        { label: "Wire Diameter", value: "1.20 – 12.0 mm" },
        { label: "Aperture Range", value: "2.0 – 150 mm" },
        { label: "Standard Alloys", value: "304, 316, 316L" },
        { label: "Crimp Patterns", value: "Lock Crimp & Flat Top" },
      ],
    },
    overview: {
      eyebrow: "Coarse Screening Engineering",
      heading: "Rigid Pre-Crimped Wire Cloth for Severe Mechanical Loads and Abrasive Sizing",
      paragraphs: [
        "Heavy-duty woven wire mesh is manufactured from heavy-gauge stainless steel wire that is precisely pre-crimped before interlacing on high-tonnage industrial looms. This pre-forming locks each wire firmly at its nodal intersection, preventing aperture distortion, wire shift, and loosening under intense vibratory motion.",
        "Available primarily in lock-crimp and flat-top weave styles, lock-crimp introduces deep corrugations on both sides of each intersection to securely clamp thick wires in place. Flat-top weave positions all knuckles on the underside, leaving a smooth, flat upper screening deck that allows bulk material to slide freely, reducing wire wear and preventing particle hang-up.",
        "Engineered for durability in corrosive wash plants, chemical sizing screens, quarry classifying decks, and heavy fluid strainers, our heavy-duty screens withstand severe impact and maintain exact aperture classification throughout extended operational campaigns.",
      ],
      keyPoints: [
        "Deep pre-crimp corrugations lock wire intersections firmly to eliminate aperture spread during vibratory motion",
        "Flat-top configurations reduce friction on the screening face to accelerate material transit and minimize abrasive wear",
        "Supplied in hook-strip tensioned screen decks, framed panels, or flat cut pieces tailored to vibrating equipment",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "Heavy-gauge stainless steel lock-crimp wire mesh screening panel",
      },
    },
    specTable: {
      heading: "Standard Heavy-Duty Screen Specifications",
      intro: "Common aperture and wire combinations in heavy-duty stainless steel screening mesh.",
      columns: [
        { key: "aperture", label: "Clear Opening (mm)", align: "left" },
        { key: "wireDia", label: "Wire Diameter (mm)", align: "right" },
        { key: "openArea", label: "Nominal Open Area (%)", align: "right" },
        { key: "crimpType", label: "Crimp Style", align: "left" },
        { key: "application", label: "Typical Industrial Role", align: "left" },
      ],
      rows: [
        {
          aperture: "3.15 mm",
          wireDia: "1.60",
          openArea: "44",
          crimpType: "Lock Crimp",
          application: "Fine aggregate & chemical pellet screening",
        },
        {
          aperture: "6.30 mm",
          wireDia: "2.50",
          openArea: "51",
          crimpType: "Lock Crimp",
          application: "Mineral grading & vibratory separator decks",
        },
        {
          aperture: "12.5 mm",
          wireDia: "3.15",
          openArea: "64",
          crimpType: "Flat Top",
          application: "Crushed stone sizing & continuous abrasive feed",
        },
        {
          aperture: "25.0 mm",
          wireDia: "5.00",
          openArea: "70",
          crimpType: "Flat Top",
          application: "Heavy scalping screens & intake water straining",
        },
      ],
      caption: "Apertures and wire diameters manufactured in compliance with ISO 9044 and ASTM E2016 tolerances.",
    },
    features: {
      heading: "Structural Advantages in Demanding Screening",
      intro: "Key manufacturing and operational benefits of heavy-duty pre-crimped screens.",
      items: [
        {
          title: "Rigid Nodal Locking",
          text: "Pre-crimped indentations seat wires securely at every intersection, preventing aperture creep under repeated cyclic impact.",
        },
        {
          title: "Superior Wear Resistance",
          text: "Heavy-gauge austenitic stainless steels resist both mechanical abrasive scratching and corrosive wash-water environments.",
        },
        {
          title: "Custom Edge Preparations",
          text: "Available with welded sheet-metal hooks, rubber-shrouded hook strips, or reinforced raw edges for side- or end-tensioned vibratory decks.",
        },
        {
          title: "Optimized Flow Velocity",
          text: "Flat-top variants allow high material velocity across the deck, preventing near-size blinding and maintaining high throughput.",
        },
      ],
    },
    related: {
      heading: "Related Specialty Weaves & Sibling Architectures",
      intro: "Review sibling specialty weave architectures designed for precision filtration and custom equipment.",
      cards: [
        {
          title: "Specialty Woven Mesh",
          text: "Explore the comprehensive family of engineered wire cloth products developed for critical process environments.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Parent Category",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Specialty precision stainless steel woven wire mesh",
          },
        },
        {
          title: "Five-Heddle Weave",
          text: "5-harness satin weave wire cloth featuring a smooth surface on one side for rapid cake release in pressure leaf filters.",
          to: "/FiveHeddleWeavePage",
          badge: "Smooth Surface",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Five-heddle stainless steel woven wire mesh structure",
          },
        },
        {
          title: "Custom Woven Mesh",
          text: "Bespoke warp/shute combinations, tailored aperture dimensions, and specialized alloy meshes configured to order.",
          to: "/CustomWovenMeshPage",
          badge: "Bespoke Solutions",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Precision custom engineered stainless steel woven wire mesh",
          },
        },
      ],
    },
    cta: {
      id: "heavy-duty-cta",
      eyebrow: "Industrial Screening Consultation",
      heading: "Request Specifications for Heavy-Duty Woven Mesh",
      lead: "Our engineers assist you in selecting the ideal wire diameter, crimp style, and edge hook configuration for your screening decks.",
      secondaryLabel: "Specialty Weaves Overview",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Standard sheets and custom tensioned hooked screens supplied to specification.",
    },
  },

  zh: {
    breadcrumbKey: "HeavyDutyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "特种编织丝网",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "重型编织网",
      lead: "采用粗线径不锈钢丝经精密预弯轧花织造而成，具备极高的机械抗冲击力与开孔稳定性，广泛应用于重负荷颗粒分级与苛刻磨损筛分。",
      secondaryCta: {
        label: "特种编织网总览",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "工业重型不锈钢预弯轧花编织丝网",
      },
      cardNote: "预弯成型高刚度结构",
      cardLink: {
        label: "筛网选型咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "丝径范围", value: "1.20 – 12.0 mm" },
        { label: "孔径跨度", value: "2.0 – 150 mm" },
        { label: "常用材质", value: "304, 316, 316L" },
        { label: "轧花形式", value: "锁弯轧花 & 平顶" },
      ],
    },
    overview: {
      eyebrow: "粗筛工程结构",
      heading: "坚固的预弯轧花丝网结构：抵御严苛机械冲击与强磨损工况",
      paragraphs: [
        "重型编织网采用粗线径高韧性不锈钢丝，在织网机编织前预先通过模具进行精密预弯压花。这种预弯工艺将经纬丝交汇点紧紧咬合卡死，彻底解决了粗丝编织在强振动工况下容易跑丝、松弛及网孔变形的问题。",
        "产品主要包括锁弯轧花（Lock Crimp）与平顶轧花（Flat Top）两大形式。锁弯形式在交织点两侧压出深凹槽，使粗丝强力互锁；平顶形式则将所有交织凸起集中在底面，上表面平整光滑，物料滑移阻力小，显著降低丝径磨损并防止颗粒挂网卡孔。",
        "重型轧花网特别适合应用于腐蚀性清洗水洗筛、化工颗粒分级、采石选矿振动筛机以及工业进水粗滤拦截网中，在承受重物料砸击与强烈磨蚀的同时，长期保持筛分分级精度稳定。",
      ],
      keyPoints: [
        "深凹槽预弯互锁节点，杜绝筛网在强高频振动中发生经纬位移与孔径变形",
        "平顶结构使筛分面平滑，加速物料过流，显著减轻磨蚀磨损并避免物料滞留",
        "支持制作带金属拉钩的张紧筛板、焊接框架筛板或按设备尺寸定制的平裁件",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "工业级重型不锈钢轧花编织筛网细节",
      },
    },
    specTable: {
      heading: "重型轧花筛网典型规格参数",
      intro: "重型不锈钢预弯轧花筛网的常用孔径与丝径组合。",
      columns: [
        { key: "aperture", label: "开孔孔径 (mm)", align: "left" },
        { key: "wireDia", label: "丝径 (mm)", align: "right" },
        { key: "openArea", label: "公称开孔率 (%)", align: "right" },
        { key: "crimpType", label: "轧花工艺形式", align: "left" },
        { key: "application", label: "典型工业用途", align: "left" },
      ],
      rows: [
        {
          aperture: "3.15 mm",
          wireDia: "1.60",
          openArea: "44",
          crimpType: "锁弯轧花 (Lock Crimp)",
          application: "精细骨料分级与化工造粒颗粒筛分",
        },
        {
          aperture: "6.30 mm",
          wireDia: "2.50",
          openArea: "51",
          crimpType: "锁弯轧花 (Lock Crimp)",
          application: "选矿分级与振动筛脱水筛面",
        },
        {
          aperture: "12.5 mm",
          wireDia: "3.15",
          openArea: "64",
          crimpType: "平顶轧花 (Flat Top)",
          application: "破碎石料分选与高磨损连续给料",
        },
        {
          aperture: "25.0 mm",
          wireDia: "5.00",
          openArea: "70",
          crimpType: "平顶轧花 (Flat Top)",
          application: "大块重负荷初级粗筛与泵口拦污格栅",
        },
      ],
      caption: "孔径公差符合 ISO 9044 与 ASTM E2016 标准。可按订单要求定制特殊孔径与丝径配比。",
    },
    features: {
      heading: "重负荷工况结构优势",
      intro: "重型预弯轧花筛网在重载振动与磨损工况中的核心优势。",
      items: [
        {
          title: "节点刚性互锁",
          text: "预弯成型凹槽将粗丝交接点牢固锁定，避免强振动力导致的筛孔逐渐变大或松脱。",
        },
        {
          title: "卓越耐磨与耐蚀",
          text: "选用 304、316 及 316L 优质不锈钢丝，耐受矿物研磨刮擦与酸碱湿法水洗冲刷。",
        },
        {
          title: "专业包边与张紧钩",
          text: "提供镀锌铁皮钩、不锈钢板折弯钩或带橡胶衬垫的拉钩，匹配各类横向或纵向张紧筛机。",
        },
        {
          title: "优化物料过流速率",
          text: "平顶筛面阻力极小，有效提升临界颗粒透筛速度，减少卡孔并提高单台筛机处理量。",
        },
      ],
    },
    related: {
      heading: "相关特种编织结构与同级产品",
      intro: "了解特种编织网体系中的其他结构，满足精密脱水与非标定制需求。",
      cards: [
        {
          title: "特种编织丝网",
          text: "了解特种编织丝网完整分类，涵盖缎纹网、重载轧花网及工程定制编织网。",
          to: "/SpecialtyWovenMeshPage",
          badge: "上级分类",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "特种精密不锈钢编织丝网",
          },
        },
        {
          title: "五综编织丝网",
          text: "1/4 斜缎纹织造，正面平整光滑便于滤饼彻底脱落，是叶滤机固液分离的核心滤材。",
          to: "/FiveHeddleWeavePage",
          badge: "平滑卸料",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "五综不锈钢编织丝网微观织造结构示意图",
          },
        },
        {
          title: "定制编织网",
          text: "按设备装配要求定制孔径、开孔率与特种丝径比的非标工程编织丝网。",
          to: "/CustomWovenMeshPage",
          badge: "工程定制",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "高精度定制工程不锈钢编织丝网",
          },
        },
      ],
    },
    cta: {
      id: "heavy-duty-cta",
      eyebrow: "工业筛分选型",
      heading: "获取重型编织轧花网规格报价与技术方案",
      lead: "请联系我们的工程师，为您匹配适用的孔径、丝径、轧花类型及筛机张紧包边形式。",
      secondaryLabel: "返回特种编织总览",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "支持按筛机图纸加工成卷或带拉钩张紧筛板。",
    },
  },

  es: {
    breadcrumbKey: "HeavyDutyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Malla de alambre especial",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Malla tejida de servicio pesado",
      lead: "Criba de alambre grueso de acero inoxidable preondulado con alta precisión, concebida para resistir impactos severos, clasificación de áridos y tamizado bajo alta abrasión.",
      secondaryCta: {
        label: "Malla especial general",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "Malla de alambre tejida preondulada industrial de servicio pesado",
      },
      cardNote: "Estructura preondulada de alta rigidez",
      cardLink: {
        label: "Asesoría para cribado",
        to: "/ContactPage",
      },
      specs: [
        { label: "Diámetro de alambre", value: "1.20 – 12.0 mm" },
        { label: "Rango de abertura", value: "2.0 – 150 mm" },
        { label: "Aleaciones estándar", value: "304, 316, 316L" },
        { label: "Tipos de ondulado", value: "Trabado y plano" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería de cribado pesado",
      heading: "Malla metálica preondulada para cargas mecánicas extremas y abrasión continua",
      paragraphs: [
        "La malla tejida de servicio pesado se fabrica a partir de alambres gruesos de acero inoxidable preondulados mediante matrices de precisión antes de su entrelazado. Este proceso de conformado fija con firmeza cada nudo de cruce, evitando el desplazamiento de los alambres y la alteración de las aperturas bajo vibración intensa.",
        "Se produce principalmente en dos patrones: ondulado trabado (Lock Crimp) y superficie plana (Flat Top). El ondulado trabado genera acanaladuras profundas que aprisionan los hilos firmemente; el tipo plano sitúa todos los relieves en la cara inferior, dejando una superficie superior lisa que facilita el desplazamiento de los materiales a granel y reduce el desgaste por rozamiento.",
        "Especialmente diseñada para plantas de lavado corrosivas, tamices de clasificación química, cribas en canteras y filtros de entrada para grandes caudales, nuestra malla de alta resistencia mantiene la precisión de corte frente a impactos intensos.",
      ],
      keyPoints: [
        "Ondulaciones trabadas que inmovilizan los puntos de cruce e impiden la dilatación de las aberturas",
        "Superficie de cribado plana que reduce la fricción, acelera el tránsito y minimiza el desgaste abrasivo",
        "Disponibilidad con ganchos de chapa para tensión lateral o longitudinal en cribas vibratorias",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "Detalle de panel de malla de acero inoxidable ondulada de servicio pesado",
      },
    },
    specTable: {
      heading: "Especificaciones representativas de mallas de servicio pesado",
      intro: "Combinaciones comunes de apertura y diámetro de alambre en mallas preonduladas de acero inoxidable.",
      columns: [
        { key: "aperture", label: "Abertura libre (mm)", align: "left" },
        { key: "wireDia", label: "Diámetro de alambre (mm)", align: "right" },
        { key: "openArea", label: "Área abierta nominal (%)", align: "right" },
        { key: "crimpType", label: "Tipo de ondulado", align: "left" },
        { key: "application", label: "Aplicación habitual", align: "left" },
      ],
      rows: [
        {
          aperture: "3.15 mm",
          wireDia: "1.60",
          openArea: "44",
          crimpType: "Ondulado trabado (Lock Crimp)",
          application: "Clasificación de finos y gránulos químicos",
        },
        {
          aperture: "6.30 mm",
          wireDia: "2.50",
          openArea: "51",
          crimpType: "Ondulado trabado (Lock Crimp)",
          application: "Cribas vibratorias y deshidratación mineral",
        },
        {
          aperture: "12.5 mm",
          wireDia: "3.15",
          openArea: "64",
          crimpType: "Superficie plana (Flat Top)",
          application: "Clasificación de áridos con alta abrasión",
        },
        {
          aperture: "25.0 mm",
          wireDia: "5.00",
          openArea: "70",
          crimpType: "Superficie plana (Flat Top)",
          application: "Precribado pesado y retención de sólidos en tomas de agua",
        },
      ],
      caption: "Aberturas y diámetros fabricados de acuerdo con ISO 9044 y ASTM E2016.",
    },
    features: {
      heading: "Ventajas estructurales para trabajo pesado",
      intro: "Puntos clave de rendimiento en cribado industrial severo.",
      items: [
        {
          title: "Inmovilización nodal rígida",
          text: "Las acanaladuras preformadas asientan los alambres con exactitud para que las luces no se abran durante el uso.",
        },
        {
          title: "Resistencia mecánica y química",
          text: "Fabricada en aceros inoxidables 304, 316 y 316L, resistente al desgaste abrasivo y a aguas de lavado corrosivas.",
        },
        {
          title: "Ganchos y refuerzos a medida",
          text: "Suministro con ganchos doblados en chapa galvanizada o inoxidable para su montaje directo en tamizadoras.",
        },
        {
          title: "Rendimiento de flujo elevado",
          text: "La superficie plana reduce la fricción, permitiendo evacuar el producto rápidamente y evitando el atascamiento.",
        },
      ],
    },
    related: {
      heading: "Mallas especiales y variantes relacionadas",
      intro: "Explore las demás variantes que completan nuestra gama de tejidos metálicos especiales.",
      cards: [
        {
          title: "Malla tejida especial",
          text: "Gama completa de telas metálicas técnicas para condiciones mecánicas y químicas complejas.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Categoría principal",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Malla de alambre tejida especial de acero inoxidable",
          },
        },
        {
          title: "Tejido de cinco lizos",
          text: "Armure satin para desprendimiento rápido de torta y mínima fricción en filtros de hojas a presión.",
          to: "/FiveHeddleWeavePage",
          badge: "Superficie lisa",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Diagrama de estructura de malla de cinco lizos",
          },
        },
        {
          title: "Malla tejida a medida",
          text: "Aberturas, relaciones de hilo y aleaciones configuradas conforme al diseño específico de su equipo.",
          to: "/CustomWovenMeshPage",
          badge: "Ingeniería a medida",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Malla tejida de acero inoxidable a medida",
          },
        },
      ],
    },
    cta: {
      id: "heavy-duty-cta",
      eyebrow: "Asesoramiento técnico",
      heading: "Solicitar cotización para malla de servicio pesado",
      lead: "Consulte con nuestro equipo para definir aberturas, diámetros de alambre y sistemas de fijación adaptados a su criba vibratoria.",
      secondaryLabel: "Ver mallas especiales",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Paneles con ganchos tensores y piezas cortadas disponibles bajo plano.",
    },
  },

  de: {
    breadcrumbKey: "HeavyDutyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Spezial-Drahtgewebe",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Schwerlast-Drahtgewebe",
      lead: "Dickdrähtige Edelstahlsiebe mit präziser Vorkröpfung, konzipiert für Schüttgutabsiebung, Mineralienklassierung und schwere abrasive Beanspruchung.",
      secondaryCta: {
        label: "Spezialgewebe Übersicht",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "Industrielles vorgekröpftes Schwerlast-Drahtgewebe aus Edelstahl",
      },
      cardNote: "Formstabile Vorkröpfung",
      cardLink: {
        label: "Siebauslegung anfragen",
        to: "/ContactPage",
      },
      specs: [
        { label: "Drahtdurchmesser", value: "1,20 – 12,0 mm" },
        { label: "Maschenweiten", value: "2,0 – 150 mm" },
        { label: "Standardlegierungen", value: "304, 316, 316L" },
        { label: "Gewebeformen", value: "Schlosskröpfung & Flat Top" },
      ],
    },
    overview: {
      eyebrow: "Grobsiebung & Schwerlasttechnik",
      heading: "Vorgekröpftes Drahtgewebe für höchste dynamische Beanspruchung und Abrasionsbeständigkeit",
      paragraphs: [
        "Schwerlast-Drahtgewebe werden aus dickdrähtigen Edelstahlqualitäten gewebt, die vor dem Webprozess präzise vorgekröpft werden. Diese Formgebung fixiert die Kreuzungspunkte formschlüssig und verhindert das Verschieben der Drähte oder eine Weitung der Maschen bei starken Vibrationen.",
        "Häufig eingesetzte Ausführungen sind die Schlosskröpfung (Lock Crimp) und die Flachkopfkröpfung (Flat Top). Bei der Schlosskröpfung umschließen tiefe Prägungen jeden Draht fest; beim Flat-Top-Gewebe liegen alle Erhebungen auf der Unterseite, sodass eine glatte Sieboberfläche entsteht, die den Materialfluss beschleunigt und den Drahtverschleiß verringert.",
        "Ausgelegt für abrasive Waschprozesse, chemische Feststoffklassierung, Steinbruchsiebe und grobe Einlaufseiher behalten diese Siebe auch bei hoher mechanischer Beanspruchung ihre Trennschärfe.",
      ],
      keyPoints: [
        "Formschlüssige Schlosskröpfung verhindert Maschenverzug unter starken dynamischen Rüttelkräften",
        "Glatte Sieboberfläche bei Flat-Top-Geweben mindert Reibung und beschleunigt den Schüttgutdurchsatz",
        "Lieferung als spannfertige Siebböden mit Spannfalzen oder als maßhaltige plane Zuschnitte",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "Schwerlast-Edelstahlsiebboden mit Schlosskröpfung",
      },
    },
    specTable: {
      heading: "Typische Spezifikationen für Schwerlast-Drahtgewebe",
      intro: "Gängige Kombinationen aus Maschenweite und Drahtstärke bei vorgekröpften Edelstahlsieben.",
      columns: [
        { key: "aperture", label: "Maschenweite (mm)", align: "left" },
        { key: "wireDia", label: "Drahtstärke (mm)", align: "right" },
        { key: "openArea", label: "Freie Siebfläche (%)", align: "right" },
        { key: "crimpType", label: "Kröpfungsart", align: "left" },
        { key: "application", label: "Typischer Einsatzbereich", align: "left" },
      ],
      rows: [
        {
          aperture: "3,15 mm",
          wireDia: "1,60",
          openArea: "44",
          crimpType: "Schlosskröpfung (Lock Crimp)",
          application: "Feine Sand- & Granulatklassierung",
        },
        {
          aperture: "6,30 mm",
          wireDia: "2,50",
          openArea: "51",
          crimpType: "Schlosskröpfung (Lock Crimp)",
          application: "Mineraliensiebung & Entwässerungssiebe",
        },
        {
          aperture: "12,5 mm",
          wireDia: "3,15",
          openArea: "64",
          crimpType: "Flachkopf (Flat Top)",
          application: "Schotterklassierung bei starkem Verschleiß",
        },
        {
          aperture: "25,0 mm",
          wireDia: "5,00",
          openArea: "70",
          crimpType: "Flachkopf (Flat Top)",
          application: "Grobrostsiebung & industrielle Einlaufrechen",
        },
      ],
      caption: "Toleranzen nach ISO 9044 und ASTM E2016.",
    },
    features: {
      heading: "Konstruktive Vorteile im Schwerlasteinsatz",
      intro: "Eigenschaften für robuste industrielle Klassierprozesse.",
      items: [
        {
          title: "Stabile Knotenpunktverriegelung",
          text: "Präzise Kröpfungen arretieren die Drähte und bewahren die exakte Maschenweite auch bei Stoßbelastung.",
        },
        {
          title: "Hohe Verschleiß- & Rostbeständigkeit",
          text: "Austenitische Edelstähle 304, 316 und 316L bieten Schutz gegen Nasskorrosion und mechanische Abrasion.",
        },
        {
          title: "Passgenaue Spannfalze",
          text: "Ausstattung mit Blechfalzen für Quer- oder Längsspannung passend zu marktüblichen Siebmaschinen.",
        },
        {
          title: "Hoher Durchsatz",
          text: "Reibungsarme Oberflächen verringern das Steckkornrisiko und halten die Siebleistung konstant hoch.",
        },
      ],
    },
    related: {
      heading: "Verwandte Spezialgewebe",
      intro: "Entdecken Sie weitere Bindungsarten unseres technischen Spezialprogramms.",
      cards: [
        {
          title: "Spezial-Drahtgewebe",
          text: "Vollständige Übersicht spezialisierter Webarten für komplexe Trenn- und Filtrationsanforderungen.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Hauptkategorie",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Präzisions-Spezialdrahtgewebe aus Edelstahl",
          },
        },
        {
          title: "Fünfschaftgewebe (Five-Heddle)",
          text: "Glatte Oberfläche für leichten Kuchenabwurf in Druckblattfiltern und zylindrischen Filterkerzen.",
          to: "/FiveHeddleWeavePage",
          badge: "Glatte Oberfläche",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Strukturdiagramm für Fünfschaftgewebe",
          },
        },
        {
          title: "Kundenspezifisches Gewebe",
          text: "Individuell abgestimmte Maschenweiten und Drahtstärken nach Ihren anlagentechnischen Vorgaben.",
          to: "/CustomWovenMeshPage",
          badge: "Sonderanfertigung",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Kundenspezifisch gewebtes Drahtgewebe",
          },
        },
      ],
    },
    cta: {
      id: "heavy-duty-cta",
      eyebrow: "Siebtechnikberatung",
      heading: "Schwerlast-Drahtgewebe anfragen",
      lead: "Unsere Fachexperten helfen Ihnen bei der Auswahl von Drahtdurchmesser, Kröpfungsart und Spannhaken für Ihre Siebanlage.",
      secondaryLabel: "Spezialgewebe ansehen",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Siebböden mit Spannfalzen und plane Zuschnitte nach Maß lieferbar.",
    },
  },

  fr: {
    breadcrumbKey: "HeavyDutyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Toile métallique spéciale",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Toile tissée renforcée (Heavy-Duty)",
      lead: "Toile de criblage en fils d'acier inoxydable de fort diamètre, pré-ondulés avec précision pour le classement de granulats et le tamisage sous forte abrasion.",
      secondaryCta: {
        label: "Toiles spéciales générales",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "Toile métallique tissée pré-ondulée industrielle pour service lourd",
      },
      cardNote: "Structure pré-ondulée indéformable",
      cardLink: {
        label: "Assistance criblage",
        to: "/ContactPage",
      },
      specs: [
        { label: "Diamètre de fil", value: "1,20 – 12,0 mm" },
        { label: "Plage d'ouverture", value: "2,0 – 150 mm" },
        { label: "Alliages standards", value: "304, 316, 316L" },
        { label: "Types d'ondulation", value: "Crantée & Flat Top" },
      ],
    },
    overview: {
      eyebrow: "Ingénierie du criblage lourd",
      heading: "Toiles métalliques pré-ondulées pour charges mécaniques intenses et milieux abrasifs",
      paragraphs: [
        "La toile tissée pour service lourd est façonnée à partir de fils d'acier inoxydable de fort diamètre, pré-ondulés sur matrices avant leur assemblage sur métiers lourds. Ce procédé verrouille chaque intersection, éliminant tout déplacement de fil et toute altération des mailles sous vibrations répétées.",
        "Deux styles d'ondulation dominent : l'ondulation crantée (Lock Crimp) et l'ondulation à surface plane (Flat Top). L'ondulation crantée maintient fermement les fils dans des crans profonds ; le style Flat Top rassemble les ondulations sur la face inférieure, offrant une face supérieure plane qui favorise le glissement des matériaux et limite l'usure par frottement.",
        "Destinées aux laveurs corrosifs, aux cribles de calibrage de minerais et aux filtres grossiers de prise d'eau, nos toiles résistent aux chocs massifs tout en conservant une précision de coupure rigoureuse.",
      ],
      keyPoints: [
        "Verrouillage indéformable aux nœuds empêchant l'élargissement des ouvertures sous vibrations violentes",
        "Face de criblage plane réduisant l'usure abrasive et favorisant l'évacuation rapide des charges",
        "Fourniture en panneaux équipés de crochets de tension adaptés aux cribles vibrants standards",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "Panneau de crible métallique ondulé lourd en inox",
      },
    },
    specTable: {
      heading: "Spécifications de toiles métalliques renforcées",
      intro: "Associations d'ouvertures et de diamètres de fil courantes pour toiles pré-ondulées en acier inoxydable.",
      columns: [
        { key: "aperture", label: "Ouverture utile (mm)", align: "left" },
        { key: "wireDia", label: "Diamètre de fil (mm)", align: "right" },
        { key: "openArea", label: "Surface ouverte (%)", align: "right" },
        { key: "crimpType", label: "Type d'ondulation", align: "left" },
        { key: "application", label: "Usage industriel", align: "left" },
      ],
      rows: [
        {
          aperture: "3,15 mm",
          wireDia: "1,60",
          openArea: "44",
          crimpType: "Ondulation crantée (Lock Crimp)",
          application: "Criblage de fines et granulés chimiques",
        },
        {
          aperture: "6,30 mm",
          wireDia: "2,50",
          openArea: "51",
          crimpType: "Ondulation crantée (Lock Crimp)",
          application: "Calibrage minier et égouttage vibrant",
        },
        {
          aperture: "12,5 mm",
          wireDia: "3,15",
          openArea: "64",
          crimpType: "Surface plane (Flat Top)",
          application: "Concassage et charges hautement abrasives",
        },
        {
          aperture: "25,0 mm",
          wireDia: "5,00",
          openArea: "70",
          crimpType: "Surface plane (Flat Top)",
          application: "Scalpage lourd et crépines d'aspiration",
        },
      ],
      caption: "Ouvertures et diamètres fabriqués selon les normes ISO 9044 et ASTM E2016.",
    },
    features: {
      heading: "Atouts techniques pour environnements sévères",
      intro: "Spécificités pour procédés industriels lourds.",
      items: [
        {
          title: "Blocage indéréglable",
          text: "Les ondulations calibrées maintiennent l'écartement exact des mailles face aux impacts répétés de matériaux lourds.",
        },
        {
          title: "Résistance combinée",
          text: "Nuances AISI 304, 316 et 316L assurant une tenue conjointe à l'abrasion et aux agressions chimiques humides.",
        },
        {
          title: "Crochets de tensionage",
          text: "Réalisation de plis latéraux en tôle inox ou galvanisée pour adaptation directe sur cribles à tension transversale ou longitudinale.",
        },
        {
          title: "Vitesse d'écoulement",
          text: "La surface plane limite les blocages de particules et maintient des débits de traitement élevés.",
        },
      ],
    },
    related: {
      heading: "Toiles spéciales associées",
      intro: "Découvrez nos autres toiles techniques pour procédés de séparation liquide ou solide.",
      cards: [
        {
          title: "Toiles métalliques spéciales",
          text: "Ensemble de notre offre en toiles métalliques haute performance pour procédés industriels.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Catégorie mère",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Toile tissée inox de haute précision pour filtration spéciale",
          },
        },
        {
          title: "Tissage 5 cadres (Five-Heddle)",
          text: "Armure satin à face lisse pour l'évacuation rapide du gâteau sur filtres sous pression.",
          to: "/FiveHeddleWeavePage",
          badge: "Surface lisse",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Schéma structurel de toile métallique 5 cadres",
          },
        },
        {
          title: "Toile tissée sur mesure",
          text: "Fabrication spéciale d'ouvertures et de diamètres de fil selon les exigences de vos carters.",
          to: "/CustomWovenMeshPage",
          badge: "Sur mesure",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Structure de toile métallique en inox sur mesure",
          },
        },
      ],
    },
    cta: {
      id: "heavy-duty-cta",
      eyebrow: "Conseil en criblage",
      heading: "Demander une étude pour toile métallique renforcée",
      lead: "Nos spécialistes vous accompagnent pour définir le diamètre de fil, le type d'ondulation et les crochets adaptés à vos cribles.",
      secondaryLabel: "Voir les toiles spéciales",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Panneaux agrafés et découpes planes sur plan disponibles sous commande.",
    },
  },

  pt: {
    breadcrumbKey: "HeavyDutyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Telas metálicas especiais",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Tela tecida de alta resistência (Heavy-Duty)",
      lead: "Tela de arame grosso de aço inoxidável pré-ondulado com alta precisão, desenvolvida para classificação de agregados, mineração e peneiramento sob forte abrasão.",
      secondaryCta: {
        label: "Telas especiais geral",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "Malha de arame tecida pré-ondulada industrial de aço inoxidável para serviço pesado",
      },
      cardNote: "Construção rígida pré-ondulada",
      cardLink: {
        label: "Consultoria de peneiramento",
        to: "/ContactPage",
      },
      specs: [
        { label: "Diâmetro do arame", value: "1,20 – 12,0 mm" },
        { label: "Faixa de abertura", value: "2,0 – 150 mm" },
        { label: "Ligas padrão", value: "304, 316, 316L" },
        { label: "Tipos de ondulação", value: "Travada e Flat Top" },
      ],
    },
    overview: {
      eyebrow: "Engenharia de peneiramento pesado",
      heading: "Tela de arame pré-ondulado para altas cargas dinâmicas e desgaste abrasivo",
      paragraphs: [
        "A tela de arame para serviço pesado é confeccionada com fios grossos de aço inoxidável, pré-ondulados com precisão antes da tecelagem em teares de grande porte. Essa deformação prévia trava firmemente as interseções dos fios, impedindo que vibrações severas desloquem os fios ou alterem as dimensões das aberturas.",
        "Os modelos predominantes são a ondulação travada (Lock Crimp) e a de topo plano (Flat Top). A ondulação travada apresenta rebaixos profundos que abraçam os arames com firmeza; o padrão de topo plano aloca os ressaltos na face inferior, proporcionando uma superfície de contato plana que agiliza o fluxo de materiais e minimiza o atrito.",
        "Projetada para instalações de lavagem úmida com fluidos agressivos, telas vibratórias de mineração e desarenadores industriais, nossa tela suporta impactos mecânicos contínuos preservando rigorosamente a faixa de corte.",
      ],
      keyPoints: [
        "Nós travados que impedem o afrouxamento e a variação da abertura sob vibrações vigorosas",
        "Superfície plana que diminui o desgaste por fricção e acelera o descolamento dos materiais",
        "Fornecimento em painéis prontos com ganchos de fixação para telas vibratórias industriais",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
        alt: "Painel de tela de aço inoxidável pré-ondulada de alta resistência",
      },
    },
    specTable: {
      heading: "Especificações de telas para serviço pesado",
      intro: "Combinações usuais de abertura e bitola de arame em telas pré-onduladas de aço inoxidável.",
      columns: [
        { key: "aperture", label: "Abertura livre (mm)", align: "left" },
        { key: "wireDia", label: "Diâmetro do arame (mm)", align: "right" },
        { key: "openArea", label: "Área aberta nominal (%)", align: "right" },
        { key: "crimpType", label: "Tipo de ondulação", align: "left" },
        { key: "application", label: "Aplicação principal", align: "left" },
      ],
      rows: [
        {
          aperture: "3,15 mm",
          wireDia: "1,60",
          openArea: "44",
          crimpType: "Ondulação travada (Lock Crimp)",
          application: "Classificação de finos e grânulos químicos",
        },
        {
          aperture: "6,30 mm",
          wireDia: "2,50",
          openArea: "51",
          crimpType: "Ondulação travada (Lock Crimp)",
          application: "Peneiramento mineral e desaguamento",
        },
        {
          aperture: "12,5 mm",
          wireDia: "3,15",
          openArea: "64",
          crimpType: "Topo plano (Flat Top)",
          application: "Classificação de britas e sólidos abrasivos",
        },
        {
          aperture: "25,0 mm",
          wireDia: "5,00",
          openArea: "70",
          crimpType: "Topo plano (Flat Top)",
          application: "Pré-classificação pesada e retenção em captações de água",
        },
      ],
      caption: "Aberturas e diâmetros em conformidade com as normas ISO 9044 e ASTM E2016.",
    },
    features: {
      heading: "Diferenciais estruturais para serviços pesados",
      intro: "Qualidades para peneiramento contínuo sob impacto.",
      items: [
        {
          title: "Fixação nodal firme",
          text: "Os vincos pré-formados impedem que os arames se soltem durante o impacto de materiais volumosos.",
        },
        {
          title: "Durabilidade mecânica e química",
          text: "Aços inoxidáveis AISI 304, 316 e 316L para suporte conjunto contra corrosão úmida e desgaste por abrasão.",
        },
        {
          title: "Ganchos de montagem",
          text: "Acabamentos com dobras de chapa para tensionamento longitudinal ou transversal nas peneiras vibratórias.",
        },
        {
          title: "Alto rendimento operacional",
          text: "A superfície uniforme previne o entupimento precoce das malhas e mantém a vazão nominal de produto.",
        },
      ],
    },
    related: {
      heading: "Telas especiais correlatas",
      intro: "Conheça outras telas estruturadas para necessidades de separação industrial.",
      cards: [
        {
          title: "Telas metálicas tecidas especiais",
          text: "Visão abrangente de geometrias de tecido metálico para processos industriais críticos.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Linha principal",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Tela tecida de aço inoxidável de precisão especial",
          },
        },
        {
          title: "Tecelagem de 5 liços",
          text: "Trama de cetim com face de trabalho lisa para descarga facilitada em filtros de folha.",
          to: "/FiveHeddleWeavePage",
          badge: "Face lisa",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Diagrama estrutural de malha de cinco liços",
          },
        },
        {
          title: "Tela tecida sob medida",
          text: "Aberturas sob medida e arranjos especiais de arame configurados para os requisitos do projeto.",
          to: "/CustomWovenMeshPage",
          badge: "Sob medida",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Tela de arame de aço inoxidável personalizada",
          },
        },
      ],
    },
    cta: {
      id: "heavy-duty-cta",
      eyebrow: "Engenharia de peneiramento",
      heading: "Solicitar orçamento para tela de alta resistência",
      lead: "Nossa equipe técnica auxilia na seleção da bitola de arame, tipo de ondulação e ganchos tensores para o seu equipamento.",
      secondaryLabel: "Explorar telas especiais",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Painéis com ganchos tensores e peças planas sob desenho fornecidos sob encomenda.",
    },
  },
};
